import { formatNumber } from "@/lib/format";
import { EventRow } from "../types/webstoreAnalytics";
import { Row, TablePanel, Td, Th } from "./AnalyticsTable";

interface EventTableProps {
  events: EventRow[];
}

/**
 * Every event name seen in the range.
 *
 * Names outside the registered vocabulary are flagged rather than hidden. Upstream
 * accepts them on purpose — the storefront and the analytics gateway ship from
 * separate pipelines, so rejecting an unlisted name would silently discard every new
 * event between a frontend release and a config update. A flag here is how that
 * shows up: it means the gateway's app registry needs the name added.
 */
export function EventTable({ events }: EventTableProps) {
  const unknownCount = events.filter((e) => !e.is_known).length;

  return (
    <TablePanel
      title="Events"
      subtitle="Every tracked action, by name"
      isEmpty={events.length === 0}
      empty="No events in this range"
      right={
        unknownCount > 0 ? (
          <span className="pill bg-amber-50 text-amber-700 border border-amber-200">
            {unknownCount} unregistered
          </span>
        ) : undefined
      }
      head={
        <>
          <Th>Event</Th>
          <Th align="right">Count</Th>
          <Th align="right">Sessions</Th>
          <Th align="right">Visitors</Th>
          <Th align="right">Per session</Th>
        </>
      }
    >
      {events.map((event) => (
        <Row key={event.event_name}>
          <Td className="font-medium text-slate-800">
            <div className="flex items-center gap-2">
              <span>{event.label}</span>
              {!event.is_known && (
                <span
                  className="text-[10px] text-accent-amber"
                  title="Not in the gateway's registered event list for webstore — add it to analytics-apps.yml"
                >
                  unregistered
                </span>
              )}
            </div>
            <div className="num-mono text-[10px] text-slate-400 mt-0.5">{event.event_name}</div>
          </Td>
          <Td align="right">{formatNumber(event.event_count)}</Td>
          <Td align="right">{formatNumber(event.sessions)}</Td>
          <Td align="right">{formatNumber(event.unique_visitors)}</Td>
          <Td align="right">
            {event.sessions ? (event.event_count / event.sessions).toFixed(1) : "—"}
          </Td>
        </Row>
      ))}
    </TablePanel>
  );
}
